/**
 * ResumableImports — Phase 2.D operator panel.
 *
 * Lists `import_checkpoints` records with status ∈ {in_progress,
 * resumable, failed}. One-click "Resume" button re-enqueues the
 * import using the backend's POST /api/imports/{upload_id}/resume.
 *
 * For PDF imports the resume action redirects the operator to the
 * PDF Extractions page (where `retry-failed` lives) since PDF resume
 * is page-level, not row-level.
 */
import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { ArrowsClockwise, ArrowCounterClockwise, Link as LinkIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const STATUS_VARIANT = {
  in_progress: "info",
  resumable: "warning",
  failed: "error",
  completed: "success",
  completed_with_failures: "warning",
};

// R1.2 — friendly label for the partial-success terminal state.
const STATUS_LABEL = {
  completed_with_failures: "Completed with failures",
};

export default function ResumableImports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(new Set());
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/imports/checkpoints");
      setItems(data.checkpoints || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load checkpoints");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resume = async (ck) => {
    if (ck.import_type === "pdf") {
      // PDF workflow removed per May 2026 decision (CSV/XLSX-only).
      toast.error("PDF imports are disabled in this build. Re-upload as CSV/XLSX.");
      return;
    }
    setResuming((s) => new Set([...s, ck.upload_id]));
    try {
      const { data } = await api.post(`/imports/${ck.upload_id}/resume`);
      toast.success(`Resumed · job ${data.job_id.slice(-6)} from offset ${data.resuming_from_offset}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed");
    } finally {
      setResuming((s) => {
        const next = new Set([...s]);
        next.delete(ck.upload_id);
        return next;
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="resumable-imports-page">
      <PageHeader
        title="Resumable Imports"
        subtitle="Long-running imports that exited mid-stream. Resume picks up from the last successful batch — no double-inserts thanks to row-level dedupe."
        actions={
          <button className="gf-btn gf-btn-outline" onClick={load} data-testid="refresh-checkpoints">
            <ArrowsClockwise size={14}/> Refresh
          </button>
        }
      />

      <div className="gf-card">
        <div className="overflow-auto">
          <table className="gf-table" data-testid="checkpoints-table">
            <thead>
              <tr>
                <th>UPDATED</th>
                <th>UPLOAD ID</th>
                <th>TYPE</th>
                <th>STATUS</th>
                <th className="text-right">ROWS / PAGES</th>
                <th className="text-right">INSERTED</th>
                <th className="text-right">FAILED</th>
                <th className="text-right">DUPLICATES</th>
                <th className="text-right">RETRY</th>
                <th>ERROR</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="text-center text-zinc-400 py-8">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={11} className="text-center text-zinc-400 py-8">No imports need resuming.</td></tr>
              )}
              {items.map((c) => (
                <tr key={c.upload_id} data-testid={`checkpoint-row-${c.upload_id}`}>
                  <td className="font-mono text-xs">{c.updated_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="font-mono text-xs max-w-[180px] truncate" title={c.upload_id}>{c.upload_id}</td>
                  <td className="font-mono text-xs">{c.import_type}</td>
                  <td><Badge variant={STATUS_VARIANT[c.status] || "info"}>{STATUS_LABEL[c.status] || c.status}</Badge></td>
                  <td className="gf-num font-mono text-xs">
                    {c.import_type === "pdf"
                      ? `p ${c.last_page_number ?? 0} / ${c.pages_total ?? "?"}`
                      : `r ${c.last_row_offset ?? 0} / ${c.rows_total ?? "?"}`}
                  </td>
                  <td className="gf-num">{c.rows_inserted ?? 0}</td>
                  <td className="gf-num">
                    {(c.rows_failed || 0) > 0 ? <Badge variant="error">{c.rows_failed}</Badge> : "—"}
                  </td>
                  <td className="gf-num">{c.duplicate_count ?? 0}</td>
                  <td className="gf-num">{c.retry_count ?? 0}</td>
                  <td className="text-xs text-red-700 max-w-[260px] truncate" title={c.error || ""}>{c.error || ""}</td>
                  <td className="text-right">
                    {c.import_type === "pdf" ? (
                      <button
                        className="gf-btn gf-btn-outline"
                        onClick={() => resume(c)}
                        data-testid={`resume-pdf-${c.upload_id}`}
                        title="PDF resume happens from the PDF Extractions page"
                      >
                        <LinkIcon size={14}/> PDF page
                      </button>
                    ) : (
                      <button
                        className="gf-btn gf-btn-primary"
                        onClick={() => resume(c)}
                        disabled={c.status === "completed" || c.status === "completed_with_failures" || resuming.has(c.upload_id)}
                        data-testid={`resume-${c.upload_id}`}
                      >
                        <ArrowCounterClockwise size={14}/> {resuming.has(c.upload_id) ? "Resuming…" : "Resume"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
